import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { FEATURE_ACCOMPAGNEMENT } from "../config/features";

// ------ Liste articles ------
export default function Journal() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/blog").then(r => {
      const raw = r.data.items || [];
      // Filter articles that promote the disabled accompagnement service
      const visible = raw.filter(a => FEATURE_ACCOMPAGNEMENT || a.cta_service !== "accompagnement");
      setItems(visible);
      setLoading(false);
    })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="journal-page" className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <p className="font-hand text-2xl text-[#A3876A]">Journal</p>
      <h1 className="font-serif text-5xl md:text-6xl text-[#2C2621] mb-6">Un peu d'encre, un peu d'attention</h1>
      <p className="text-lg text-[#4A4036] max-w-2xl mb-16">
        Des mots doux, des invitations à écrire, des lectures, des rituels — pour prendre soin de toi à travers l'écriture.
      </p>

      {loading && <p className="text-[#7C7064]">Chargement…</p>}
      {!loading && items.length === 0 && (
        <p className="text-[#7C7064]">Le premier article arrive très bientôt.</p>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
        {items.map(a => (
          <Link key={a.slug} to={`/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`}
            className="group block">
            {a.cover_image && (
              <img src={a.cover_image} alt={a.title} className="w-full h-56 object-cover mb-5 group-hover:opacity-90 transition-opacity"/>
            )}
            {!a.cover_image && (
              <div className="w-full h-56 bg-[#F5F0E6] mb-5 flex items-center justify-center">
                <p className="font-hand text-3xl text-[#A3876A]">✦</p>
              </div>
            )}
            <p className="text-xs text-[#7C7064] uppercase tracking-widest mb-2">{new Date(a.created_at).toLocaleDateString("fr-FR", {day: "numeric", month: "long", year: "numeric"})}</p>
            <h2 className="font-serif text-2xl text-[#2C2621] mb-3 group-hover:text-[#A3876A] transition-colors">{a.title}</h2>
            {a.excerpt && <p className="text-[#4A4036] leading-relaxed line-clamp-3">{a.excerpt}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ------ Article détail ------
export function JournalArticle() {
  const { slug } = useParams();
  const [art, setArt] = useState(null);
  const [err, setErr] = useState(null);
  const [hide, setHide] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then(r => {
        // Hide accompagnement-focused articles when the feature is off
        if (!FEATURE_ACCOMPAGNEMENT && r.data.cta_service === "accompagnement") {
          setHide(true);
          return;
        }
        setArt(r.data);
        // SEO — dynamic head tags
        const a = r.data;
        document.title = (a.seo_title || `${a.title} — Les RDV de l'écriture`);
        const setMeta = (name, content, attr = "name") => {
          if (!content) return;
          let el = document.querySelector(`meta[${attr}="${name}"]`);
          if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
          el.setAttribute("content", content);
        };
        setMeta("description", a.seo_description || a.excerpt || "");
        setMeta("keywords", (a.keywords || []).join(", "));
        setMeta("og:title", a.title, "property");
        setMeta("og:description", a.seo_description || a.excerpt || "", "property");
        setMeta("og:type", "article", "property");
        setMeta("og:url", `https://lesrdvdelecriture.com/journal/${a.slug}`, "property");
        if (a.cover_image) setMeta("og:image", a.cover_image, "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", a.title);
        setMeta("twitter:description", a.seo_description || a.excerpt || "");

        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute("href", `https://lesrdvdelecriture.com/journal/${a.slug}`);

        // JSON-LD Article + Breadcrumb
        const oldArt = document.getElementById("article-jsonld"); if (oldArt) oldArt.remove();
        const oldBc = document.getElementById("breadcrumb-jsonld"); if (oldBc) oldBc.remove();

        const artScript = document.createElement("script");
        artScript.type = "application/ld+json";
        artScript.id = "article-jsonld";
        artScript.text = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": a.title,
          "description": a.seo_description || a.excerpt || "",
          "image": a.cover_image ? [a.cover_image] : undefined,
          "author": { "@type": "Person", "name": "Amélie", "url": "https://lesrdvdelecriture.com/a-propos" },
          "publisher": {
            "@type": "Organization",
            "name": "Les Rendez-vous de l'écriture",
            "logo": { "@type": "ImageObject", "url": "https://lesrdvdelecriture.com/logo-sceau.png" }
          },
          "datePublished": a.created_at,
          "dateModified": a.updated_at || a.created_at,
          "mainEntityOfPage": `https://lesrdvdelecriture.com/journal/${a.slug}`,
          "keywords": (a.keywords || []).join(", "),
          "inLanguage": "fr-FR"
        });
        document.head.appendChild(artScript);

        const bcScript = document.createElement("script");
        bcScript.type = "application/ld+json";
        bcScript.id = "breadcrumb-jsonld";
        bcScript.text = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://lesrdvdelecriture.com/" },
            { "@type": "ListItem", "position": 2, "name": "Le Journal", "item": "https://lesrdvdelecriture.com/journal" },
            { "@type": "ListItem", "position": 3, "name": a.title, "item": `https://lesrdvdelecriture.com/journal/${a.slug}` }
          ]
        });
        document.head.appendChild(bcScript);
      })
      .catch(() => setErr("Article introuvable"));
    return () => {
      document.getElementById("article-jsonld")?.remove();
      document.getElementById("breadcrumb-jsonld")?.remove();
    };
  }, [slug]);

  if (hide) return <Navigate to="/journal" replace/>;
  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-4xl mb-4">Introuvable</h1>
      <Link to="/journal" className="btn-primary">Retour au journal</Link>
    </div>
  );
  if (!art) return <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#7C7064]">Chargement…</div>;

  // Render markdown-lite:
  //   ## Heading   →  <h2>
  //   ### Heading  →  <h3>
  //   - / * bullet →  <ul>
  //   [text](/x)   →  internal <Link>
  //   [text](https://...) → external <a>
  //   **bold** / *italic*
  const raw = (art.content || "").replace(/\r\n/g, "\n");
  const blocks = raw.split(/\n\n+/).map(b => b.trim()).filter(Boolean);

  const safeUrl = (url) => /^(https?:\/\/|mailto:|\/)/i.test(url.trim()) ? url.trim() : "#";
  const renderInline = (txt) => {
    // Bold ** ** and italic * *
    let html = txt
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");
    // Links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
      url = safeUrl(url);
      const isInternal = url.startsWith("/");
      if (isInternal) return `<a data-internal="1" href="${url}" class="text-[#A3876A] underline underline-offset-4 hover:text-[#8A7158]">${label}</a>`;
      return `<a href="${url}" target="_blank" rel="noopener" class="text-[#A3876A] underline underline-offset-4 hover:text-[#8A7158]">${label}</a>`;
    });
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "em", "a", "br"], ALLOWED_ATTR: ["href", "target", "rel", "class", "data-internal"] });
  };

  const renderBlock = (b, i) => {
    if (b.startsWith("## ")) {
      return <h2 key={i} className="font-serif text-2xl md:text-3xl text-[#2C2621] mt-12 mb-4 leading-tight"
                 dangerouslySetInnerHTML={{__html: renderInline(b.substring(3))}}/>;
    }
    if (b.startsWith("### ")) {
      return <h3 key={i} className="font-serif text-xl text-[#2C2621] mt-8 mb-3" dangerouslySetInnerHTML={{__html: renderInline(b.substring(4))}}/>;
    }
    if (/^[-*] /.test(b)) {
      const items = b.split("\n").filter(l => /^[-*] /.test(l)).map(l => l.replace(/^[-*] /, ""));
      return <ul key={i} className="list-disc pl-6 space-y-2 text-[#2C2621] leading-relaxed text-lg mb-6">
        {items.map((li) => <li key={li} dangerouslySetInnerHTML={{__html: renderInline(li)}}/>)}
      </ul>;
    }
    const withBr = b.replace(/\n/g, "<br/>");
    return <p key={i} className="text-[#2C2621] leading-relaxed text-lg mb-6" dangerouslySetInnerHTML={{__html: renderInline(withBr)}}/>;
  };

  return (
    <article data-testid="journal-article" className="max-w-3xl mx-auto px-6 md:px-12 py-16">
      <Link to="/journal" className="text-sm text-[#A3876A] hover:underline">← Retour au journal</Link>
      <p className="text-xs text-[#7C7064] uppercase tracking-widest mt-8 mb-3">
        {new Date(art.created_at).toLocaleDateString("fr-FR", {day: "numeric", month: "long", year: "numeric"})}
      </p>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#2C2621] leading-tight mb-6">{art.title}</h1>
      {art.excerpt && <p className="text-xl text-[#4A4036] italic mb-10">{art.excerpt}</p>}
      {art.cover_image && (
        <img src={art.cover_image} alt={art.title} className="w-full h-80 object-cover mb-12"/>
      )}
      <div className="prose max-w-none">
        {blocks.map((b, i) => renderBlock(b, i))}
      </div>
      {art.tags && art.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-[#E8DCC8]">
          <p className="text-sm text-[#7C7064]">{art.tags.map(t => `#${t}`).join(" ")}</p>
        </div>
      )}
      <div className="mt-16 bg-[#F5F0E6] p-8 text-center">
        {art.cta_service === "correspondance" ? (
          <>
            <p className="font-hand text-2xl text-[#A3876A] mb-3">Envie d'échanger de vraies lettres ?</p>
            <p className="text-[#4A4036] mb-6">Découvre notre petite correspondance — je te réponds à la main sous deux semaines.</p>
            <Link to="/correspondance" className="btn-primary">Découvrir la correspondance</Link>
          </>
        ) : art.cta_service === "accompagnement" && FEATURE_ACCOMPAGNEMENT ? (
          <>
            <p className="font-hand text-2xl text-[#A3876A] mb-3">Besoin d'aide pour trouver vos mots ?</p>
            <p className="text-[#4A4036] mb-6">Discours, hommage, lettre importante — un premier échange téléphonique est offert.</p>
            <Link to="/accompagnement" className="btn-primary">Découvrir l'accompagnement</Link>
          </>
        ) : art.cta_service === "mailclub" ? (
          <>
            <p className="font-hand text-2xl text-[#A3876A] mb-3">Envie d'un rendez-vous chaque mois ?</p>
            <p className="text-[#4A4036] mb-6">Le Club de Lettres : une enveloppe, des mots, une invitation à écrire — 12 €/mois, sans engagement.</p>
            <Link to="/club-de-lettres" className="btn-primary" data-testid="article-cta-mailclub">Découvrir le Club de Lettres</Link>
          </>
        ) : (
          <>
            <p className="font-hand text-2xl text-[#A3876A] mb-3">Envie d'écrire à ton tour ?</p>
            <p className="text-[#4A4036] mb-6">Découvre les programmes d'écriture, 52 rendez-vous doux.</p>
            <Link to="/programme" className="btn-primary">Découvrir le programme</Link>
          </>
        )}
      </div>
    </article>
  );
}
